/* eslint-disable no-nested-ternary */
import React, { useState } from 'react'
import {
  BodyText,
  Card,
  Button,
  HeadingText,
  Checkbox,
  Alert,
} from '@paypalcorp/pp-react'

//Project Import
import AccessGranted from './access-granted'
import Spinner from '../../common/spinner'
import { recordCplEnd, recordDynamicEvent } from '../../common/analytics/utils'
import { SELLER_INVITATION_ACTION } from '../../../common/cpl-event-names'
import EmailConfirmationAlert from './email-confirmation-alert'
import { useApplicationContext } from '../../../context/ApplicationContext'
import { FPTI_COMPONENTS_CATEGORIES } from '../../../common/constant'
import { Message } from '@paypalcorp/worldready-react'
import logUtil from '../../../utils/log-util'
import InvitationRestricted from './invitation-restricted'

function AcceptInvitation({
  invite,
  acceptedSheet,
  isLoading,
  closeSheet,
  confirmEmailURL,
}) {
  const { isConfirmedEmail } = useApplicationContext()
  const [checkValue, setCheckValue] = useState(false)
  const [isAccepted, setIsAccepted] = useState(false)
  const [isRestricted, setIsRestricted] = useState(false)
  const [errorInvite, setErrorInvite] = useState(null)
  const payload = JSON.parse(invite?.application_context?.transaction_payload)
  const approvalNotes = invite?.initiator_notes
  const inviterName = payload?.inviterName
  const invitingBusinessName = payload?.invitingBusinessName
  const onCloseInviteAlert = () => {
    recordDynamicEvent(
      `${FPTI_COMPONENTS_CATEGORIES.ALERT_ERROR},click,close-alert,developer-invite`,
    )
    setErrorInvite(null)
  }

  const onAccept = async (status, inv) => {
    try {
      if (!checkValue && status === 'APPROVED') {
        return
      }
      logUtil.info(`start of accept/reject invitation`)

      const response = await acceptedSheet(status, inv)
      if (response === 'SUCCESS') {
        logUtil.info(`invitation approved successfully`)
        setIsAccepted(true)
      } else if (response === 'RESTRICTED') {
        setIsRestricted(true)
      } else {
        logUtil.info(`invitation rejected successfully`)
      }
    } catch (error) {
      recordCplEnd('accept/reject/invitation', {
        cpl_event: 'accept/reject invitation',
        status: error.status || 404,
        event_name: SELLER_INVITATION_ACTION,
      })
      logUtil.error(`error while accepting/rejecting invitation`)
      setErrorInvite('error')
    }
  }

  const handleOnChange = () => {
    setCheckValue(!checkValue)
  }

  const checkboxLabel =
    "You confirm you're employed by or have a contract with the seller. " +
    "You understand you can use your account(s) for as long as you're authorized, " +
    'and the seller is responsible for your usage. ' +
    "We'll securely store your information according to our Privacy Statement."

  let content
  if (isLoading) {
    content = <Spinner fullScreen={true} size="xl" />
  } else if (!isConfirmedEmail) {
    content = (
      <EmailConfirmationAlert
        closeModal={closeSheet}
        emailURL={confirmEmailURL}
      />
    )
  } else if (isRestricted) {
    content = <InvitationRestricted closeSheet={closeSheet} />
  } else if (isAccepted) {
    content = <AccessGranted acceptedInvited={invite} closeSheet={closeSheet} />
  } else {
    content = (
      <div
        style={{
          marginBottom: '10px',
        }}
      >
        <HeadingText size="lg">
          <Message id="developer-invite.accept.invitation.title" />
        </HeadingText>
        {errorInvite && (
          <Alert
            className="view_invitation-alert"
            type="warning"
            closeButton={{
              'aria-label': 'dismiss alert',
              onClick: onCloseInviteAlert,
            }}
            data-testid="alert-error"
          >
            <Message id="developer-invite.serverError" />
          </Alert>
        )}
        <BodyText style={{ marginTop: '20px' }}>
          <b>{inviterName}</b> invited you to <b>{invitingBusinessName}</b> as a
          developer.
        </BodyText>

        {approvalNotes && (
          <>
            <BodyText strong style={{ marginTop: '20px' }}>
              {`Message from ${inviterName}`}
            </BodyText>
            <Card
              style={{
                marginTop: '15px',
                marginBottom: '20px',
                padding: '5px',
              }}
            >
              <Card.Content>
                <BodyText>
                  <ul>
                    <li>{approvalNotes}</li>
                  </ul>
                </BodyText>
              </Card.Content>
            </Card>
          </>
        )}

        <div
          style={{
            marginBottom: '20px',
            marginTop: approvalNotes ? '0px' : '32px',
          }}
        >
          <Checkbox
            label={checkboxLabel}
            name="seller-invitation"
            checked={checkValue}
            onChange={handleOnChange}
            data-testid="seller-invitation-checkbox"
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            secondary
            style={{ marginRight: '12px' }}
            onClick={() => onAccept('REJECTED', invite)}
            data-dxa="decline_invite,decline,click_decline_invite"
            data-testid="declineInvite"
          >
            Decline
          </Button>
          <Button
            style={{ opacity: checkValue ? 1 : 0.3 }}
            onClick={() => onAccept('APPROVED', invite)}
            data-dxa="accept_invite,accept,click_accept_invite"
            data-testid="acceptInvite"
          >
            Accept
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div>{content}</div>
    </>
  )
}

export default AcceptInvitation
